/**
 * DbServerChecksList — lista kontroli serwera baz (wynik „Sprawdź połączenie" / „Przygotuj serwer")
 * z czytelnymi nazwami; nieznana nazwa kontroli jest pokazywana tak, jak przyszła z serwera.
 */

import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';

import type { DbServerCheckResult } from '@/lib/db-servers/api';

const CHECK_LABEL_KEYS: Record<string, string> = {
  app_connect_postgres: 'admin.dbServers.checks.app_connect_postgres',
  app_role_attributes: 'admin.dbServers.checks.app_role_attributes',
  server_version_16: 'admin.dbServers.checks.server_version_16',
  ssl_in_use: 'admin.dbServers.checks.ssl_in_use',
  saas_dict_ro_admin_option: 'admin.dbServers.checks.saas_dict_ro_admin_option',
  saas_proc_rw_admin_option: 'admin.dbServers.checks.saas_proc_rw_admin_option',
  saas_dict_ro_connect: 'admin.dbServers.checks.saas_dict_ro_connect',
  saas_proc_rw_connect: 'admin.dbServers.checks.saas_proc_rw_connect',
};

export function DbServerChecksList({ result }: { result: DbServerCheckResult }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" data-testid="db-server-checks">
      {result.serverVersion && (
        <p className="text-xs text-text-tertiary">
          {t('admin.dbServers.checks.serverVersion', { version: result.serverVersion })}
        </p>
      )}
      <ul className="flex flex-col divide-y divide-border-secondary rounded-lg border border-border-secondary">
        {result.checks.map((c) => {
          const key = CHECK_LABEL_KEYS[c.name];
          return (
            <li
              key={c.name}
              className="flex items-start gap-2 px-3 py-2"
              data-testid={`db-server-check-${c.name}`}
            >
              {c.ok ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              )}
              <div className="min-w-0">
                <p className="text-sm text-text-primary">{key ? t(key) : c.name}</p>
                {c.detail && <p className="break-words text-xs text-text-tertiary">{c.detail}</p>}
              </div>
              <span className="sr-only">
                {c.ok ? t('admin.dbServers.checks.ok') : t('admin.dbServers.checks.failed')}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
